// 検索条件を共有するためのState (URLへの反映は別途)
// 設定は1項目毎に更新されることもあるのでリアクティブにしない。
import { NormalizeDay, type CircleSearchParam, type CircleSearchParamForState, type SortOptionsType } from "~/types/entity/cWebCatalog";

const getDefaultValue = (): CircleSearchParamForState => ({
  // event_id: 0,
  keyword: undefined,
  day: undefined,
  area: "",
  blocksCsv: "",
  genresCsv: "",
  canUseElectronicPayment: undefined,
  favoriteColor: undefined,
  favoriteMemo: undefined,
  sort: undefined, // 未指定。実効ソートは resolveCircleSort で解決する (ADR-020)
  rankingScoreThreshold: undefined,
  // count: 100,
  page: 1, // ページは設定されていなくても既定で 1 を返す
  wcid: undefined,
  // prev: undefined,
    // 詳細検索オプション
  isSearchCircleName: true,
  isSearchAuthor: true,
  isSearchProductInfo: true,
  view: undefined
});

export const useCircleSearchState = () => {
  const state = useState<CircleSearchParamForState>("circleSearchParam", getDefaultValue);

  const reset = () => {
    state.value = getDefaultValue();
  };

  // 検索情報のクリアだけでソート順は変更しない。
  const resetSearch = () => {
    const sort = state.value.sort;
    const rankingScoreThreshold = state.value.rankingScoreThreshold;
    const def = getDefaultValue();
    state.value = {
      ...def,
      sort: sort,
      rankingScoreThreshold: rankingScoreThreshold
    }
    ;
  };

  // 以下の set??? 関数は値の変更がないときには代入しない。更新のループを抑制するため

  const setPage = (page: number) => {
    if (state.value.page !== page) {
      state.value = {
        ...state.value,
        page: page
      };
    }
  };

  const setGenre = (genres: string[]) => {
    const genresCsv = genres.join(",");
    if (state.value.genresCsv !== genresCsv) {
      state.value = {
        ...state.value,
        genresCsv: genresCsv,
      };
    }
  };

  const setDay = (day: number | undefined) => {
    const dayNormalized = NormalizeDay(day);
    if (state.value.day !== dayNormalized) {
      state.value = {
        ...state.value,
        day: dayNormalized
      };
    }
  };

  const setFavoriteColor = (favoriteColor: number | undefined) => {
    if (state.value.favoriteColor !== favoriteColor) {
      state.value = {
        ...state.value,
        favoriteColor: favoriteColor,
      };
    }
  };

  const setSort = (sort: SortOptionsType) => {
    if (state.value.sort !== sort) {
      state.value = {
        ...state.value,
        sort: sort,
      };
    }
  };

  /**
   * genres, blocks が配列とカンマの変換を要する
   * 既定値の埋め込みは 初期値設定 と 型定義 で確保される
   */
  const getCircleSearchParam = (): CircleSearchParam => ({
    ...state.value,
    genres: state.value.genresCsv ? state.value.genresCsv?.split(",") : undefined,
    blocks: state.value.blocksCsv ? state.value.blocksCsv.split(",") : undefined,
  });

  const setCircleSearchParam = (v: CircleSearchParam): Readonly<CircleSearchParam> => {
    state.value = {
      keyword: v.keyword,
      day: NormalizeDay(v.day),
      area: v.area,
      genresCsv: v.genres?.join(",") ?? "",
      blocksCsv: v.blocks?.join(",") ?? "",
      canUseElectronicPayment: v.canUseElectronicPayment,
      favoriteColor: v.favoriteColor,
      favoriteMemo: v.favoriteMemo,
      sort: v.sort,
      // count: v.count,
      rankingScoreThreshold: v.rankingScoreThreshold,
      page: v.page,
      wcid: v.wcid,
      isSearchCircleName: v.isSearchCircleName !== false,
      isSearchAuthor: v.isSearchAuthor !== false,
      isSearchProductInfo: v.isSearchProductInfo !== false,
      view: v.view
    };
    return getCircleSearchParam();
  };

  const getDefault = (): CircleSearchParamForState => getDefaultValue();

  // アプリケーション内でグローバルな state の操作は影響範囲が大きいので 手軽に使える値の操作として公開せず
  // 関数呼び出しとして見せる
  return {
    /**
     * パラメータとして取得する
     */
    get: getCircleSearchParam,
    /**
     * パラメータを更新する
     * @param v
     * @returns
     */
    set: setCircleSearchParam,
    // /**
    //  * watchで利用するための値を返す。この値に対して直接操作してはいけない
    //  * @returns state 自体を返す
    //  */
    // getForWatch: (): Ref<CircleSearchParam> => state,
    /**
     * 初期状態に戻す
     * ただし一部の値を維持することがある (例えば event_id)
     *
     * 1関数内で 他の値設定を続いて呼び出した場合 画面の再描画されないことが期待できる
     */
    reset,
    /**
     * 検索情報だけクリアし、ソート順に影響する情報は残す。
     * ジャンル等、検索条件を上書きして再検索したい場合。
     */
    resetSearch,
    /**
     * ページを設定する
     */
    setPage,
    /**
     * ジャンルを設定する
     */
    setGenre,
    setDay,
    setFavoriteColor,
    setSort,
    getDefault
  };
};
